import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { arisanService, memberService, transactionService, userService } from '../services/api';
import { AuthContext } from '../services/authContext';
import RouletteWheel from '../components/RouletteWheel';
import './ArisanPages.css';

const ArisanDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [arisan, setArisan] = useState(null);
  const [members, setMembers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [drawLoading, setDrawLoading] = useState(false);
  const [showDrawModal, setShowDrawModal] = useState(false);
  const [drawResult, setDrawResult] = useState(null);
  const [showRouletteModal, setShowRouletteModal] = useState(false);
  const [eligibleMembers, setEligibleMembers] = useState([]);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showManualSelectModal, setShowManualSelectModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [editForm, setEditForm] = useState({ userId: '', amount: '', receivedDate: '' });
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionType, setTransactionType] = useState('contribution');
  const [transactionForm, setTransactionForm] = useState({
    memberId: '',
    amount: '',
    notes: '',
    paymentMethod: 'cash'
  });

  useEffect(() => {
    fetchData();
    // Fetch all users for adding members (any authenticated user can see user list)
    fetchAllUsers();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [arisanRes, membersRes, transRes, summaryRes] = await Promise.all([
        arisanService.getById(id),
        memberService.getByArisan(id),
        transactionService.getByArisan(id),
        transactionService.getSummary(id)
      ]);
      
      setArisan(arisanRes.data);
      setMembers(membersRes.data);
      setTransactions(transRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load arisan');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await userService.getAllUsers();
      setAllUsers(response.data);
      console.log('Loaded all users:', response.data.length);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError('Gagal memuat daftar pengguna');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleRecordContribution = (memberId) => {
    setTransactionType('contribution');
    setTransactionForm({
      memberId: memberId,
      amount: arisan.monthlyAmount || '',
      notes: '',
      paymentMethod: 'cash'
    });
    setShowTransactionModal(true);
  };

  const handleRecordDistribution = (memberId) => {
    setTransactionType('distribution');
    setTransactionForm({
      memberId: memberId,
      amount: arisan.monthlyAmount * arisan.totalMembers || '',
      notes: '',
      paymentMethod: 'cash'
    });
    setShowTransactionModal(true);
  };

  const handleTransactionSubmit = async () => {
    if (!transactionForm.memberId || !transactionForm.amount) {
      setError('Mohon lengkapi semua field yang diperlukan');
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = {
        memberId: transactionForm.memberId,
        amount: parseFloat(transactionForm.amount),
        notes: transactionForm.notes,
        paymentMethod: transactionForm.paymentMethod
      };

      if (transactionType === 'contribution') {
        await transactionService.recordContribution(data);
        setSuccess('Iuran berhasil dicatat');
      } else {
        await transactionService.recordDistribution(data);
        setSuccess('Pembagian berhasil dicatat');
      }

      setShowTransactionModal(false);
      setTransactionForm({ memberId: '', amount: '', notes: '', paymentMethod: 'cash' });
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal mencatat transaksi');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDrawRecipient = async () => {
    setDrawLoading(true);
    setError('');
    setSuccess('');
    
    try {
      // Get eligible members (not in recipients list)
      const recipientIds = arisan.recipients.map(r => r.userId._id);
      const eligible = members
        .filter(m => m.status === 'active' && !recipientIds.includes(m.userId._id))
        .map(m => m.userId);
      
      if (eligible.length === 0) {
        setError('Tidak ada anggota yang memenuhi syarat untuk undian');
        setDrawLoading(false);
        return;
      }
      
      setEligibleMembers(eligible);
      setShowRouletteModal(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal memulai undian');
      setDrawLoading(false);
    }
  };

  const handleRouletteComplete = async (winner) => {
    try {
      const response = await arisanService.drawRecipient(id);
      setDrawResult(response.data);
      setSuccess(response.data.message);
      
      // Close roulette and show result
      setTimeout(() => {
        setShowRouletteModal(false);
        setShowDrawModal(true);
        setDrawLoading(false);
        fetchData();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal melakukan undian');
      setShowRouletteModal(false);
      setDrawLoading(false);
    }
  };

  const handleManualSelect = () => {
    setError('');
    setSuccess('');
    const recipientIds = arisan.recipients.map(r => r.userId._id);
    const eligible = members
      .filter(m => m.status === 'active' && !recipientIds.includes(m.userId._id))
      .map(m => m.userId);
    
    if (eligible.length === 0) {
      setError('Tidak ada anggota yang memenuhi syarat');
      return;
    }
    
    setEligibleMembers(eligible);
    setShowManualSelectModal(true);
  };

  const handleManualSelectSubmit = async () => {
    if (!selectedUserId) {
      setError('Pilih anggota terlebih dahulu');
      return;
    }
    
    setActionLoading(true);
    setError('');
    
    try {
      const response = await arisanService.setRecipient(id, selectedUserId);
      setDrawResult(response.data);
      setSuccess('Penerima berhasil dipilih');
      setShowManualSelectModal(false);
      setSelectedUserId('');
      setShowDrawModal(true);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal memilih penerima');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddMember = () => {
    setError('');
    setSuccess('');
    setShowAddMemberModal(true);
  };

  const handleAddMemberSubmit = async () => {
    if (!selectedUserId) {
      setError('Pilih pengguna terlebih dahulu');
      return;
    }
    
    setActionLoading(true);
    setError('');
    
    try {
      await arisanService.addMember(id, selectedUserId);
      setSuccess('Anggota berhasil ditambahkan');
      setShowAddMemberModal(false);
      setSelectedUserId('');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menambahkan anggota');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Yakin ingin menghapus anggota ini?')) {
      return;
    }
    
    setActionLoading(true);
    setError('');
    
    try {
      await arisanService.removeMember(id, memberId);
      setSuccess('Anggota berhasil dihapus');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menghapus anggota');
    } finally {
      setActionLoading(false);
    }
  };

  const closeDrawModal = () => {
    setShowDrawModal(false);
    setDrawResult(null);
  };

  const handleDeleteArisan = async () => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus arisan ini? Semua data anggota dan transaksi akan dihapus.')) {
      return;
    }
    
    setActionLoading(true);
    setError('');
    
    try {
      await arisanService.delete(id);
      setSuccess('Arisan berhasil dihapus');
      setTimeout(() => {
        navigate('/arisans');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menghapus arisan');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditRecipient = (index, recipient) => {
    setEditingRecipient(index);
    setEditForm({
      userId: recipient.userId?._id || '',
      amount: recipient.amount || '',
      receivedDate: new Date(recipient.receivedDate).toISOString().split('T')[0]
    });
  };

  const handleCancelEdit = () => {
    setEditingRecipient(null);
    setEditForm({ userId: '', amount: '', receivedDate: '' });
  };

  const handleSaveRecipient = async (index) => {
    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      const updateData = {};
      if (editForm.userId) updateData.userId = editForm.userId;
      if (editForm.amount) updateData.amount = parseFloat(editForm.amount);
      if (editForm.receivedDate) updateData.receivedDate = editForm.receivedDate;

      await arisanService.updateRecipient(id, index, updateData);
      setSuccess('Penerima berhasil diperbarui');
      setEditingRecipient(null);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal memperbarui penerima');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteRecipient = async (index) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus penerima ini dari riwayat?')) {
      return;
    }

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await arisanService.deleteRecipient(id, index);
      setSuccess('Penerima berhasil dihapus dari riwayat');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Gagal menghapus penerima');
    } finally {
      setActionLoading(false);
    }
  };

  const isCreator = arisan && user && arisan.createdBy._id === user.id;

  if (loading) return <div className="page-container"><p>Memuat...</p></div>;
  if (error) return <div className="page-container"><div className="error-message">{error}</div></div>;
  if (!arisan) return <div className="page-container"><p>Arisan tidak ditemukan</p></div>;

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>{arisan.name}</h2>
        <div className="header-actions">
          {isCreator && arisan.status === 'active' && (
            <>
              <button 
                onClick={handleDrawRecipient} 
                className="btn btn-primary"
                disabled={drawLoading}
              >
                {drawLoading ? 'Mengundi...' : '🎲 Undi Penerima'}
              </button>
              <button 
                onClick={handleManualSelect} 
                className="btn btn-secondary"
                disabled={actionLoading}
              >
                ✋ Pilih Manual
              </button>
            </>
          )}
          <button onClick={() => navigate('/arisans')} className="btn-back">← Kembali</button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Ringkasan
        </button>
        <button 
          className={`tab ${activeTab === 'members' ? 'active' : ''}`}
          onClick={() => setActiveTab('members')}
        >
          Anggota
        </button>
        <button 
          className={`tab ${activeTab === 'transactions' ? 'active' : ''}`}
          onClick={() => setActiveTab('transactions')}
        >
          Transaksi
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="tab-content">
          <div className="arisan-info">
            <div className="info-grid">
              <div className="info-card">
                <h4>Status</h4>
                <p className={`status ${arisan.status}`}>{arisan.status === 'active' ? 'Aktif' : 'Tidak Aktif'}</p>
              </div>
              <div className="info-card">
                <h4>Iuran Bulanan</h4>
                <p>Rp {arisan.monthlyAmount.toLocaleString('id-ID')}</p>
              </div>
              <div className="info-card">
                <h4>Anggota</h4>
                <p>{arisan.totalMembers}</p>
              </div>
              <div className="info-card">
                <h4>Siklus</h4>
                <p>{arisan.cycle} bulan</p>
              </div>
              <div className="info-card">
                <h4>Putaran</h4>
                <p>{arisan.currentRound}/{arisan.totalMembers}</p>
              </div>
              <div className="info-card">
                <h4>Total Iuran</h4>
                <p>Rp {summary?.totalContributions?.toLocaleString('id-ID') || 0}</p>
              </div>
              <div className="info-card">
                <h4>Total Pembagian</h4>
                <p>Rp {summary?.totalDistributions?.toLocaleString('id-ID') || 0}</p>
              </div>
            </div>
            
            {/* Recipients History */}
            {arisan.recipients && arisan.recipients.length > 0 && (
              <div className="recipients-section">
                <h4>Riwayat Penerima Arisan</h4>
                <table className="recipients-table">
                  <thead>
                    <tr>
                      <th>Putaran</th>
                      <th>Penerima</th>
                      <th>Tanggal</th>
                      <th>Jumlah</th>
                      {isCreator && <th>Aksi</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {arisan.recipients.map((recipient, index) => (
                      <tr key={index}>
                        <td>#{recipient.round}</td>
                        <td>
                          {editingRecipient === index ? (
                            <select
                              value={editForm.userId}
                              onChange={(e) => setEditForm({ ...editForm, userId: e.target.value })}
                              className="form-input"
                            >
                              <option value="">Pilih Anggota</option>
                              {arisan.members.map(member => (
                                <option key={member._id} value={member._id}>
                                  {member.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            recipient.userId?.name || 'N/A'
                          )}
                        </td>
                        <td>
                          {editingRecipient === index ? (
                            <input
                              type="date"
                              value={editForm.receivedDate}
                              onChange={(e) => setEditForm({ ...editForm, receivedDate: e.target.value })}
                              className="form-input"
                            />
                          ) : (
                            new Date(recipient.receivedDate).toLocaleDateString('id-ID')
                          )}
                        </td>
                        <td>
                          {editingRecipient === index ? (
                            <input
                              type="number"
                              value={editForm.amount}
                              onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                              className="form-input"
                              placeholder="Jumlah"
                            />
                          ) : (
                            `Rp ${recipient.amount?.toLocaleString('id-ID') || 0}`
                          )}
                        </td>
                        {isCreator && (
                          <td>
                            {editingRecipient === index ? (
                              <div className="action-buttons">
                                <button
                                  onClick={() => handleSaveRecipient(index)}
                                  className="btn-icon btn-save"
                                  disabled={actionLoading}
                                  title="Simpan"
                                >
                                  ✓
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="btn-icon btn-cancel"
                                  disabled={actionLoading}
                                  title="Batal"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <div className="action-buttons">
                                <button
                                  onClick={() => handleEditRecipient(index, recipient)}
                                  className="btn-icon btn-edit"
                                  disabled={actionLoading}
                                  title="Edit"
                                >
                                  ✎
                                </button>
                                <button
                                  onClick={() => handleDeleteRecipient(index)}
                                  className="btn-icon btn-delete-small"
                                  disabled={actionLoading}
                                  title="Hapus"
                                >
                                  🗑
                                </button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {arisan.description && (
              <div className="description">
                <h4>Deskripsi</h4>
                <p>{arisan.description}</p>
              </div>
            )}

            {isCreator && (
              <div className="danger-zone">
                <h4>⚠️ Zona Berbahaya</h4>
                <p>Menghapus arisan akan menghapus semua data anggota, transaksi, dan riwayat terkait. Tindakan ini tidak dapat dibatalkan.</p>
                <button 
                  onClick={handleDeleteArisan} 
                  className="btn btn-delete"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Menghapus...' : '🗑️ Hapus Arisan'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'members' && (
        <div className="tab-content">
          <div className="members-section">
            {isCreator && (
              <div className="section-header">
                <button onClick={handleAddMember} className="btn btn-primary">
                  ➕ Tambah Anggota
                </button>
              </div>
            )}
            <table className="members-table">
              <thead>
                <tr>
                  <th>Nama</th>
                  <th>Email</th>
                  <th>Telepon</th>
                  <th>Status</th>
                  <th>Total Dibayar</th>
                  <th>Total Diterima</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {members.map(member => (
                  <tr key={member._id}>
                    <td>{member.userId?.name}</td>
                    <td>{member.userId?.email}</td>
                    <td>{member.userId?.phoneNumber || '-'}</td>
                    <td><span className={`status ${member.status}`}>{member.status === 'active' ? 'Aktif' : 'Tidak Aktif'}</span></td>
                    <td>Rp {member.totalPaid?.toLocaleString('id-ID') || 0}</td>
                    <td>Rp {member.totalReceived?.toLocaleString('id-ID') || 0}</td>
                    <td>
                      {isCreator && (
                        <>
                          <button 
                            onClick={() => handleRecordContribution(member._id)}
                            className="btn-small"
                          >
                            Iuran
                          </button>
                          <button 
                            onClick={() => handleRecordDistribution(member._id)}
                            className="btn-small"
                          >
                            Pembagian
                          </button>
                        </>
                      )}
                      {isCreator && member.userId._id !== arisan.createdBy._id && (
                        <button 
                          onClick={() => handleRemoveMember(member.userId._id)}
                          className="btn-small btn-danger"
                          disabled={actionLoading}
                        >
                          Hapus
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'transactions' && (
        <div className="tab-content">
          <div className="transactions-section">
            <table className="transactions-table">
              <thead>
                <tr>
                  <th>Tanggal</th>
                  <th>Anggota</th>
                  <th>Tipe</th>
                  <th>Jumlah</th>
                  <th>Status</th>
                  <th>Catatan</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(transaction => (
                  <tr key={transaction._id}>
                    <td>{new Date(transaction.createdAt).toLocaleDateString('id-ID')}</td>
                    <td>{transaction.memberId?.userId?.name || '-'}</td>
                    <td><span className={`type ${transaction.type}`}>{transaction.type === 'contribution' ? 'Iuran' : 'Pembagian'}</span></td>
                    <td>Rp {transaction.amount?.toLocaleString('id-ID') || 0}</td>
                    <td><span className={`status ${transaction.status}`}>{transaction.status === 'completed' ? 'Selesai' : transaction.status === 'pending' ? 'Menunggu' : 'Gagal'}</span></td>
                    <td>{transaction.notes || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Roulette Wheel Modal */}
      {showRouletteModal && (
        <div className="modal-overlay">
          <div className="modal-content roulette-modal" onClick={(e) => e.stopPropagation()}>
            <RouletteWheel 
              members={eligibleMembers}
              onComplete={handleRouletteComplete}
            />
          </div>
        </div>
      )}

      {/* Draw Result Modal */}
      {showDrawModal && drawResult && (
        <div className="modal-overlay" onClick={closeDrawModal}>
          <div className="modal-content draw-modal" onClick={(e) => e.stopPropagation()}>
            <div className="draw-result">
              <div className="confetti">🎉</div>
              <h2>Selamat!</h2>
              <div className="winner-info">
                <h3>{drawResult.winner.name}</h3>
                <p className="winner-email">{drawResult.winner.email}</p>
              </div>
              <div className="draw-details">
                <p className="round-info">Putaran #{drawResult.round}</p>
                <p className="amount-info">
                  Jumlah: <strong>Rp {drawResult.amount?.toLocaleString('id-ID')}</strong>
                </p>
              </div>
              <button className="btn btn-primary" onClick={closeDrawModal}>
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal-overlay" onClick={() => setShowAddMemberModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Tambah Anggota</h3>
            <div className="form-group">
              <label>Pilih Pengguna:</label>
              {loadingUsers ? (
                <p>Memuat daftar pengguna...</p>
              ) : (
                <select 
                  value={selectedUserId} 
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="form-control"
                >
                  <option value="">-- Pilih Pengguna --</option>
                  {allUsers
                    .filter(u => !members.some(m => m.userId._id === u._id))
                    .map(user => (
                      <option key={user._id} value={user._id}>
                        {user.name} ({user.email})
                      </option>
                    ))
                  }
                </select>
              )}
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="modal-actions">
              <button 
                onClick={handleAddMemberSubmit} 
                className="btn btn-primary"
                disabled={actionLoading || !selectedUserId}
              >
                {actionLoading ? 'Menambahkan...' : 'Tambah'}
              </button>
              <button 
                onClick={() => {
                  setShowAddMemberModal(false);
                  setSelectedUserId('');
                  setError('');
                }} 
                className="btn btn-secondary"
                disabled={actionLoading}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Select Modal */}
      {showManualSelectModal && (
        <div className="modal-overlay" onClick={() => setShowManualSelectModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Pilih Penerima Manual</h3>
            <div className="form-group">
              <label>Pilih Anggota:</label>
              <select 
                value={selectedUserId} 
                onChange={(e) => setSelectedUserId(e.target.value)}
                className="form-control"
              >
                <option value="">-- Pilih Anggota --</option>
                {eligibleMembers.map(member => (
                  <option key={member._id} value={member._id}>
                    {member.name} ({member.email})
                  </option>
                ))}
              </select>
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="modal-actions">
              <button 
                onClick={handleManualSelectSubmit} 
                className="btn btn-primary"
                disabled={actionLoading || !selectedUserId}
              >
                {actionLoading ? 'Memproses...' : 'Pilih'}
              </button>
              <button 
                onClick={() => {
                  setShowManualSelectModal(false);
                  setSelectedUserId('');
                  setError('');
                }} 
                className="btn btn-secondary"
                disabled={actionLoading}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transaction Modal */}
      {showTransactionModal && (
        <div className="modal-overlay" onClick={() => setShowTransactionModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>{transactionType === 'contribution' ? 'Catat Iuran' : 'Catat Pembagian'}</h3>
            <div className="form-group">
              <label>Anggota:</label>
              <select 
                value={transactionForm.memberId} 
                onChange={(e) => setTransactionForm({ ...transactionForm, memberId: e.target.value })}
                className="form-control"
                disabled={transactionForm.memberId !== ''}
              >
                <option value="">-- Pilih Anggota --</option>
                {members.map(member => (
                  <option key={member._id} value={member._id}>
                    {member.userId?.name} ({member.userId?.email})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Jumlah (Rp):</label>
              <input
                type="number"
                value={transactionForm.amount}
                onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                className="form-control"
                placeholder="Masukkan jumlah"
                min="0"
              />
            </div>
            <div className="form-group">
              <label>Metode Pembayaran:</label>
              <select 
                value={transactionForm.paymentMethod} 
                onChange={(e) => setTransactionForm({ ...transactionForm, paymentMethod: e.target.value })}
                className="form-control"
              >
                <option value="cash">Tunai</option>
                <option value="transfer">Transfer</option>
                <option value="e-wallet">E-Wallet</option>
              </select>
            </div>
            <div className="form-group">
              <label>Catatan (Opsional):</label>
              <textarea
                value={transactionForm.notes}
                onChange={(e) => setTransactionForm({ ...transactionForm, notes: e.target.value })}
                className="form-control"
                rows="3"
                placeholder="Tambahkan catatan jika diperlukan"
              ></textarea>
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="modal-actions">
              <button 
                onClick={handleTransactionSubmit} 
                className="btn btn-primary"
                disabled={actionLoading || !transactionForm.memberId || !transactionForm.amount}
              >
                {actionLoading ? 'Menyimpan...' : 'Simpan'}
              </button>
              <button 
                onClick={() => {
                  setShowTransactionModal(false);
                  setTransactionForm({ memberId: '', amount: '', notes: '', paymentMethod: 'cash' });
                  setError('');
                }} 
                className="btn btn-secondary"
                disabled={actionLoading}
              >
                Batal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArisanDetailPage;
